/**
 * Страница ожидания подтверждения администратором
 * Samsung OneUI Design v2.0
 */
import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDeviceStore } from '../store/deviceStore'
import { apiClient } from '../services/apiClient'
import { DeviceRegistrationService } from '../services/deviceRegistrationService'
import './WaitingApprovalPage.css'

export function WaitingApprovalPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const deviceStore = useDeviceStore()
  const [attempts, setAttempts] = useState(0)
  const [activationToken, setActivationToken] = useState<string | null>(null)
  const [deviceName, setDeviceName] = useState<string | null>(null)
  const pollingIntervalRef = useRef<number | null>(null)

  // Получаем данные из URL параметров или localStorage
  useEffect(() => {
    const token = searchParams.get('token') || localStorage.getItem('activationToken')
    const name = searchParams.get('device_name') || localStorage.getItem('deviceName')
    
    if (token) {
      setActivationToken(token)
      localStorage.setItem('activationToken', token)
    }
    if (name) {
      setDeviceName(name)
      localStorage.setItem('deviceName', name)
    }
  }, [searchParams])

  useEffect(() => {
    // Если устройство уже зарегистрировано, редиректим на главную
    if (deviceStore.isRegistered) {
      navigate('/', { replace: true })
      return
    }

    const checkStatus = async () => {
      try {
        const deviceId = deviceStore.deviceId
        if (!deviceId) {
          console.error('[WaitingApprovalPage] No device ID')
          return
        }

        console.log('[WaitingApprovalPage] Checking registration status...')
        setAttempts(prev => prev + 1)
        
        // Проверяем статус регистрации
        const response = await apiClient.get(`/tablet/register/status/${deviceId}`) as any
        
        console.log('[WaitingApprovalPage] Status response:', response)
        
        if (response.status === 'approved') {
          console.log('[WaitingApprovalPage] Device approved! Completing registration...')
          
          // Устройство одобрено - сохраняем credentials и регистрируемся
          await DeviceRegistrationService.completeRegistration(
            response.device_id,
            response.device_secret,
            response.site_id
          )
          
          console.log('[WaitingApprovalPage] Registration complete, redirecting to scanner')
          
          // Очищаем интервал
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
          }
          
          // Редиректим на главную страницу
          navigate('/', { replace: true })
        } else if (response.status === 'rejected') {
          console.log('[WaitingApprovalPage] Device rejected by admin')
          alert('Ваш запрос на регистрацию был отклонен администратором.')
          
          // Очищаем интервал
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
          }
          
          // Редиректим на страницу с гайдом
          navigate('/registration-guide', { replace: true })
        }
        // Если status === 'pending', продолжаем поллинг
        
      } catch (error) {
        console.error('[WaitingApprovalPage] Error checking status:', error)
      }
    }

    // Первая проверка сразу
    checkStatus()
    
    // Затем проверяем каждые 5 секунд
    pollingIntervalRef.current = window.setInterval(checkStatus, 5000)
    
    // Cleanup при unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [deviceStore.deviceId, deviceStore.isRegistered, navigate])

  return (
    <div className="waiting-approval-page">
      <div className="waiting-approval-page__container">
        {/* Samsung OneUI Spinner */}
        <div className="oneui-spinner">
          <div className="oneui-spinner__ring"></div>
          <div className="oneui-spinner__ring oneui-spinner__ring--delayed"></div>
          <div className="oneui-spinner__center">
            {/* Logo removed as requested */}
          </div>
        </div>
        
        <h1 className="waiting-approval-page__title">
          Ожидание подтверждения
        </h1>
        
        <p className="waiting-approval-page__subtitle">
          Ваше устройство ожидает одобрения администратором
        </p>
        
        {/* Карточка с информацией об устройстве */}
        <div className="device-info-card">
          <div className="device-info-card__header">
            <div className="device-info-card__icon">📱</div>
            <h3 className="device-info-card__title">Информация об устройстве</h3>
          </div>
          
          <div className="device-info-card__content">
            {deviceName && (
              <div className="device-info-row">
                <span className="device-info-row__label">Название</span>
                <span className="device-info-row__value device-info-row__value--highlight">
                  {deviceName}
                </span>
              </div>
            )}
            
            {activationToken && (
              <div className="device-info-row">
                <span className="device-info-row__label">Токен активации</span>
                <span className="device-info-row__value device-info-row__value--token">
                  {activationToken}
                </span>
              </div>
            )}
            
            <div className="device-info-row">
              <span className="device-info-row__label">Device ID</span>
              <span className="device-info-row__value device-info-row__value--mono">
                {deviceStore.deviceId ? (
                  <>
                    <span className="device-info-row__value-short">
                      {deviceStore.deviceId.substring(0, 8)}...{deviceStore.deviceId.substring(deviceStore.deviceId.length - 4)}
                    </span>
                    <span className="device-info-row__value-full" title={deviceStore.deviceId}>
                      {deviceStore.deviceId}
                    </span>
                  </>
                ) : (
                  'Генерация...'
                )}
              </span>
            </div>
            
            <div className="device-info-row device-info-row--status">
              <span className="device-info-row__label">Статус</span>
              <span className="device-info-row__value">
                <span className="status-badge status-badge--pending">
                  <span className="status-badge__dot"></span>
                  Ожидание подтверждения
                </span>
              </span>
            </div>
          </div>
        </div>
        
        {/* Информационный блок */}
        <div className="info-block">
          <div className="info-block__row">
            <span className="info-block__icon">🔄</span>
            <span className="info-block__text">
              Проверка статуса каждые 5 секунд
              {attempts > 0 && <span className="info-block__attempts"> · Попытка {attempts}</span>}
            </span>
          </div>
          <div className="info-block__row">
            <span className="info-block__icon">👤</span>
            <span className="info-block__text">
              Администратор должен одобрить устройство в панели управления
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
